import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config";
import StateManagedSelect from "react-select";
import { SubmitButton } from "../../components/Input";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

// DeepLink Options
const deeplinkOptions = [
  { value: "", label: "- Select -" },
  { value: "general", label: "General" },
  { value: "portfolio", label: "Portfolio" },
  { value: "portfolio-detail", label: "Portfolio - Detail" },
  { value: "streetview", label: "Streetview" },
];

const TemplatePushNotificationForm = () => {
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  // Form states
  const [templatename, setTemplatename] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [fieldlist, setFieldlist] = useState([]);
  const [deeplink, setDeeplink] = useState({ value: "", label: "- Select -" });
  const [file, setFile] = useState(null);
  const [oldFileUrl, setOldFileUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");

  // ✅ Sender dropdown for Push Notification
  const [senderList, setSenderList] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);

  // Fetch push notification template (for edit)
  const fetchTemplatePush = async (id, pushSenders = []) => {
  setLoading(true);
  try {
    const response = await axios.get(
      `${API_URL}settings/template/pushnotification/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = response.data.data;
    setTemplatename(data.templatename || "");
    setTitle(data.title || "");
    setMessage(data.message || "");

    if (data.image_url) {
      setOldFileUrl(data.image_url);
      setImagePreview(data.image_url);
    }

    const selectedDeeplink = deeplinkOptions.find(
      (option) => option.value?.toLowerCase() === data.deeplink?.toLowerCase()
    ) || (data.deeplink ? { value: data.deeplink, label: data.deeplink } : { value: "", label: "- Select -" });
    setDeeplink(selectedDeeplink);

    // ✅ Correctly set selected sender
    if (data.sender_type && pushSenders.length > 0) {
      const foundSender = pushSenders.find(
        (opt) => opt.value === Number(data.sender_type)
      );
      setSelectedSender(foundSender || null);
    }
  } catch (error) {
    console.error("Error fetching templates:", error);
    showToast.error("Error fetching template details");
  } finally {
    setLoading(false);
  }
};

  // Fetch field list
  const fetchFieldList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}settings/template/pushnotification/dropdown`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const formattedField = response.data.data.map((field) => ({
        value: field.field_name,
        label: field.field_description,
      }));
      setFieldlist(formattedField);
    } catch (error) {
      console.error("Error fetching field list:", error);
      showToast.error("Error fetching field list");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch push notification sender list
 const fetchSenderList = async () => {
  try {
    const response = await axios.get(`${API_URL}settings/emailconfig/dropdown`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const pushSenders =
      response.data?.data?.push?.map((item) => ({
        value: Number(item.value), // convert to number to match sender_type
        label: `${item.label} (${item.sender})`,
        sender: item.sender,
      })) || [];

    setSenderList(pushSenders);

    // ✅ After loading sender list, fetch template if editing
    if (id) {
      fetchTemplatePush(id, pushSenders); // pass sender list
    }
  } catch (error) {
    console.error("Error fetching push sender list:", error);
  }
};


  useEffect(() => {
    // Load field list and sender list first
    const loadData = async () => {
      await Promise.all([fetchFieldList(), fetchSenderList()]);
    };
    loadData();
  }, []);

  // Handle file change with preview
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const previewUrl = URL.createObjectURL(selectedFile);
      setImagePreview(previewUrl);
      setOldFileUrl("");
    }
  };

  const handleClearImage = () => {
    setFile(null);
    setImagePreview("");
    setOldFileUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templatename.trim()) {
      showToast.error("Template name is required");
      return;
    }
    if (!title.trim()) {
      showToast.error("Notification title is required");
      return;
    }
    if (!message.trim()) {
      showToast.error("Message is required");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("templatename", templatename.trim());
    formData.append("title", title.trim());
    formData.append("message", message.trim());
    formData.append("deeplink", deeplink.value || "");
    formData.append("sender_type", selectedSender ? selectedSender.value : "");
    if (file) formData.append("file", file);

    try {
      const url = id
        ? `${API_URL}settings/template/pushnotification/${id}`
        : `${API_URL}settings/template/pushnotification/save`;

      const method = id ? axios.put : axios.post;

      const response = await method(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        showToast.success(response.data.message);
        navigate("/Settings/TemplatePushNotification");
      } else {
        showToast.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving template Push Notification:", error);
      const errorMessage =
        error.response?.data?.message || "There was an error saving the template";
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = (selectedOption) => {
    if (selectedOption) {
      const fieldValue = `{${selectedOption.value}}`;
      const textarea = document.querySelector('textarea[name="message"]');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newMessage =
          message.substring(0, start) + fieldValue + message.substring(end);
        setMessage(newMessage);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(
            start + fieldValue.length,
            start + fieldValue.length
          );
        }, 0);
      } else {
        setMessage((prev) => prev + fieldValue);
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form
            onSubmit={handleSubmit}
            className="stdform"
            encType="multipart/form-data"
          >
            <div className="panel-heading border-0">
              <span className="panel-title">
                {id ? "Edit " : "Add "} Template Push Notification
              </span>
            </div>

            <div className="panel-body">
              <div className="row">
                {/* Template Name */}
                <div className="col-md-4 mb-2">
                  <label>Template Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Template Name"
                    value={templatename}
                    onChange={(e) => setTemplatename(e.target.value)}
                    required
                  />
                </div>

                {/* Notification Title */}
                <div className="col-md-4 mb-2">
                  <label>Notification Title <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Notification Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                {/* ✅ Sender Dropdown */}
                <div className="col-md-4 mb-2">
                  <label>Sender Type</label>
                  <StateManagedSelect
                    options={senderList}
                    value={selectedSender}
                    onChange={setSelectedSender}
                    placeholder="Select Push Notification Sender"
                    isClearable
                  />
                </div>

                {/* DeepLink Dropdown */}
                <div className="col-md-4 mb-2">
                  <label>Deeplink To</label>
                  <StateManagedSelect
                    options={deeplinkOptions}
                    value={deeplink}
                    onChange={(opt) => setDeeplink(opt)}
                    isSearchable={false}
                  />
                </div>

                {/* Field List (Dropdown) */}
                <div className="col-md-12 mb-2">
                  <label>Add Dynamic Fields to Message</label>
                  <StateManagedSelect
                    options={fieldlist}
                    onChange={handleAddField}
                    placeholder="Select a field to insert in message"
                    isClearable
                  />
                </div>

                {/* Message */}
                <div className="col-md-12 mb-2">
                  <label>Message <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    placeholder="Enter Message. Use {field_name} for dynamic fields."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="5"
                    name="message"
                    required
                  ></textarea>
                </div>

                {/* Image Upload */}
                <div className="col-md-12 mb-2">
                  <label>Upload Image</label>
                  <div className="d-flex align-items-center gap-3">
                    <input
                      type="file"
                      className="form-control"
                      onChange={handleFileChange}
                      accept="image/*"
                      style={{ maxWidth: "300px" }}
                    />
                    {(imagePreview || oldFileUrl) && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleClearImage}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                </div>

                {/* Image Preview */}
                {(imagePreview || oldFileUrl) && (
                  <div className="col-md-12 mb-2">
                    <label>Image Preview</label>
                    <div className="mt-2">
                      <img
                        src={imagePreview || oldFileUrl}
                        alt="Preview"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="col-md-12 mt-3">
                  <SubmitButton
                    pageBack={"/Settings/TemplatePushNotification"}
                    handleSubmit={handleSubmit}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TemplatePushNotificationForm;
